/*
 * Send to Athena by page groups (owner 10-13, OSA staff: "For incoming faxes
 * with multiple pages ... classify different pages under different categories
 * (Pages 1-2 Admin, Pages 3-5 Imaging, etc) ... suggest it when they click
 * 'Send to Athena' - showing the pages in a nice ui so that they can quickly
 * move pages between categories").
 *
 * The suggestion is v2's fax-v5 read (GET fax/{id}/ai `page_segments`): the
 * pages split into documents, each with the Athena class it is filed as. The
 * classes are exactly the ones Send to Athena files today (athenaFiling.js):
 * IMAGING (imaging result) or CLINICAL + a subclass. Athena's ADMIN / LAB
 * classes have no filing route in the gateway, so they are not offered.
 *
 * A fax COVER sheet is never a document of its own here: it stays with the
 * document after it (filed with it, as today) and is only marked, so staff can
 * leave it out with one tap. A fax read as [cover, one document] therefore
 * opens as ONE group of all pages, which files the original file, byte for
 * byte, exactly as today's Send to Athena.
 *
 * Pure functions on
 *   groups:   [{ key, title, cls, pages: [n...] }]   (cls: a CLASS_OPTIONS id)
 *   excluded: [n...]                                  ("Not filed")
 */

export const CLASS_OPTIONS = Object.freeze([
  { id: "IMAGING", label: "Imaging result", category: "IMAGING", subclass: null },
  { id: "CLINICALDOCUMENT", label: "Clinical document", category: "CLINICAL", subclass: "CLINICALDOCUMENT" },
  { id: "CONSULTNOTE", label: "Consult note", category: "CLINICAL", subclass: "CONSULTNOTE" },
  { id: "ADMISSIONDISCHARGE", label: "Admission / discharge", category: "CLINICAL", subclass: "ADMISSIONDISCHARGE" },
  { id: "OPERATIVENOTE", label: "Operative note", category: "CLINICAL", subclass: "OPERATIVENOTE" },
  { id: "MENTALHEALTH", label: "Mental health", category: "CLINICAL", subclass: "MENTALHEALTH" },
  { id: "URGENTCARE", label: "Urgent care", category: "CLINICAL", subclass: "URGENTCARE" },
]);
const CLASS_BY_ID = new Map(CLASS_OPTIONS.map((c) => [c.id, c]));

export const classLabel = (id) => CLASS_BY_ID.get(id)?.label || "Clinical document";

// The modal's own Category / subtype pick as a class id, and back.
export function classOf(category, subclass) {
  if (category === "IMAGING") return "IMAGING";
  return CLASS_BY_ID.has(subclass) ? subclass : "CLINICALDOCUMENT";
}
export function categoryOf(cls) {
  const c = CLASS_BY_ID.get(cls) || CLASS_BY_ID.get("CLINICALDOCUMENT");
  return { category: c.category, subclass: c.subclass || "CLINICALDOCUMENT" };
}

const range = (a, b) => Array.from({ length: Math.max(0, b - a + 1) }, (_, i) => a + i);
let seq = 0;
export const newKey = () => `g${Date.now().toString(36)}${(seq += 1)}`;

// "1-3,5" (also the document route's ?pages=), "Page 4" / "Pages 1–3, 5" for people.
export function pagesText(pages) {
  const p = [...new Set(pages)].sort((a, b) => a - b);
  const parts = [];
  for (let i = 0; i < p.length; ) {
    let j = i;
    while (j + 1 < p.length && p[j + 1] === p[j] + 1) j += 1;
    parts.push(j > i ? `${p[i]}–${p[j]}` : String(p[i]));
    i = j + 1;
  }
  return `${p.length === 1 ? "Page" : "Pages"} ${parts.join(", ")}`;
}

/*
 * The groups the modal opens on:
 *   - one group of every page (class `fallbackCls`) without a usable
 *     suggestion (no read, one segment, pages unknown, or a suggestion that
 *     does not cover 1..numPages);
 *   - else one group per non-cover segment, a cover sheet joined to the
 *     group after it (or before it, at the end).
 * -> { groups, cover: Set of cover pages, unreadFrom, confidence, fromAi }
 */
export function initialGroups(numPages, segmentsView, fallbackCls = "CLINICALDOCUMENT") {
  const n = Number(numPages) || 0;
  const one = (pages) => ({
    groups: [{ key: newKey(), title: "", cls: fallbackCls, pages }],
    cover: new Set(),
    unreadFrom: null,
    confidence: null,
    fromAi: false,
  });
  const segs = segmentsView && Array.isArray(segmentsView.segments) ? segmentsView.segments : [];
  if (n < 1) return one([]);
  if (!segs.length) return one(range(1, n));
  // the suggestion must cover 1..n exactly (the fax may have been re-read since)
  let next = 1;
  for (const s of segs) {
    const [a, b] = Array.isArray(s.pages) ? s.pages : [];
    if (a !== next || !(b >= a)) return one(range(1, n));
    next = b + 1;
  }
  if (next - 1 !== n) return one(range(1, n));
  const cover = new Set();
  const groups = [];
  let pendingCover = [];
  segs.forEach((s) => {
    const pages = range(s.pages[0], s.pages[1]);
    if (s.category === "COVER" || s.class === "COVER") {
      pages.forEach((p) => cover.add(p));
      pendingCover = pendingCover.concat(pages);
      return;
    }
    const cls = CLASS_BY_ID.has(s.class) ? s.class : classOf(s.category, s.subclass);
    groups.push({ key: newKey(), title: String(s.title || ""), cls, pages: pendingCover.concat(pages) });
    pendingCover = [];
  });
  if (pendingCover.length) {
    if (groups.length) groups[groups.length - 1].pages = groups[groups.length - 1].pages.concat(pendingCover);
    else return { ...one(range(1, n)), cover };
  }
  if (groups.length === 1) {
    // [cover, one document]: today's one filing; keep the AI's class and title
    return { ...one(range(1, n)), groups: [{ ...groups[0], pages: range(1, n) }], cover, fromAi: true,
      confidence: segmentsView.confidence || null, unreadFrom: segmentsView.unread_from || null };
  }
  return {
    groups,
    cover,
    unreadFrom: segmentsView.unread_from || null,
    confidence: segmentsView.confidence || null,
    fromAi: true,
  };
}

const sortNum = (a) => [...a].sort((x, y) => x - y);

// Move `pages` to group `to` (a key), to a NEW group ("new"), or out
// ("excluded"). Empty groups go; the rest stay in page order.
export function movePages(state, pages, to) {
  const moving = new Set(pages);
  if (!moving.size) return state;
  const firstFrom = state.groups.findIndex((g) => g.pages.some((p) => moving.has(p)));
  let groups = state.groups.map((g) => ({ ...g, pages: g.pages.filter((p) => !moving.has(p)) }));
  let excluded = state.excluded.filter((p) => !moving.has(p));
  if (to === "excluded") {
    excluded = sortNum(excluded.concat([...moving]));
  } else if (to === "new") {
    const src = state.groups[firstFrom] || state.groups[state.groups.length - 1];
    const g = { key: newKey(), title: "", cls: src ? src.cls : "CLINICALDOCUMENT", pages: sortNum([...moving]) };
    const at = firstFrom >= 0 ? firstFrom + 1 : groups.length;
    groups = [...groups.slice(0, at), g, ...groups.slice(at)];
  } else {
    groups = groups.map((g) => (g.key === to ? { ...g, pages: sortNum(g.pages.concat([...moving])) } : g));
  }
  // documents stay in page order (a new one from pages 4-5 of 4-7 comes before 6-7)
  const kept = groups.filter((g) => g.pages.length > 0);
  kept.sort((x, y) => Math.min(...x.pages) - Math.min(...y.pages));
  return { ...state, groups: kept, excluded };
}

// Split a group at `page`: that page and the ones after it start a new group.
export function splitAt(state, key, page) {
  const i = state.groups.findIndex((g) => g.key === key);
  if (i < 0) return state;
  const g = state.groups[i];
  const head = g.pages.filter((p) => p < page);
  const tail = g.pages.filter((p) => p >= page);
  if (!head.length || !tail.length) return state;
  const groups = [...state.groups];
  groups.splice(i, 1, { ...g, pages: head }, { key: newKey(), title: "", cls: g.cls, pages: tail });
  return { ...state, groups };
}

// Merge a group into the one after it (the later group's pages join it).
export function mergeWithNext(state, key) {
  const i = state.groups.findIndex((g) => g.key === key);
  if (i < 0 || i + 1 >= state.groups.length) return state;
  const a = state.groups[i];
  const b = state.groups[i + 1];
  const groups = [...state.groups];
  groups.splice(i, 2, { ...a, title: a.title || b.title, pages: sortNum(a.pages.concat(b.pages)) });
  return { ...state, groups };
}

export function updateGroup(state, key, patch) {
  return { ...state, groups: state.groups.map((g) => (g.key === key ? { ...g, ...patch } : g)) };
}

/*
 * What Upload / "File N documents" sends: one filing per non-empty group, in
 * order. allPages: the group is the whole fax (the original file is sent, as
 * today, never a cut). note: Athena's internal note — the staff note alone
 * for a single whole-fax filing (today's behaviour), else "<title or class>
 * (pages a–b)" before it.
 */
export function filingsOf(state, numPages, staffNote = "") {
  const n = Number(numPages) || 0;
  const groups = state.groups.filter((g) => g.pages.length > 0);
  const note = (staffNote || "").trim();
  return groups.map((g) => {
    const pages = sortNum(g.pages);
    const allPages = n > 0 ? pages.length === n && pages[0] === 1 && pages[n - 1] === n : groups.length === 1;
    const { category, subclass } = categoryOf(g.cls);
    const what = (g.title || "").trim() || classLabel(g.cls);
    const label = `${what} (${pagesText(pages).toLowerCase()})`;
    return {
      key: g.key,
      pages,
      allPages,
      category,
      subclass,
      cls: g.cls,
      label,
      internalNote: allPages && groups.length === 1 ? note : note ? `${label} — ${note}` : label,
    };
  });
}
